import type { ReactNode } from 'react';
import styles from './Badge.module.css';

/**
 * Badge con dos variantes de FORMA, no solo de color:
 *
 *   solid   → estado del ticket. Píldora rellena con punto.
 *   outline → prioridad / urgencia. Chip con contorno, sin punto.
 *
 * La forma es lo que las distingue. Un estado y una prioridad pueden caer en
 * el mismo tono (ámbar "En progreso" junto a ámbar "Prioridad media" es el
 * caso real que aparece en la cabecera del detalle) y aun así no se confunden,
 * porque una va rellena y la otra no.
 *
 * Cada pantalla mapea su dominio a un `tone`. Ningún tono debe ser 'blue' para
 * un estado: el azul significa "se puede pulsar".
 */
export type BadgeTone = 'blue' | 'amber' | 'green' | 'red' | 'purple' | 'teal' | 'slate';
export type BadgeVariant = 'solid' | 'outline';

interface BadgeProps {
  tone: BadgeTone;
  /** 'solid' (estado, por defecto) · 'outline' (prioridad / urgencia). */
  variant?: BadgeVariant;
  children: ReactNode;
  /** Tooltip nativo opcional (p.ej. para explicar un indicador ⚠). */
  title?: string;
}

export function Badge({ tone, variant = 'solid', children, title }: BadgeProps) {
  const cls = [styles.badge, styles[tone], variant === 'outline' && styles.outline]
    .filter(Boolean)
    .join(' ');
  return <span className={cls} title={title}>{children}</span>;
}
